import { useState } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { Navigate } from 'react-router-dom'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { ExternalLinkIcon } from 'lucide-react'
import {
  Button,
  Box,
  Card,
  Fieldset,
  Form,
  FormInputField,
  LabeledCheckbox,
  Splash,
  Stack,
  TextLink,
  toast,
} from '@joyful/ui/core'
import {
  AuthSplashSignOutButton,
  useCurrentUser,
  useApiQueryClient,
  useApiMutation,
} from '@joyful/ui/app'
import { extractErrorMessage } from '@joyful/utils'

const WelcomeNameSchema = z.object({
  firstName: z.string().trim(),
  lastName: z.string().trim(),
})

type WelcomeNameValues = z.infer<typeof WelcomeNameSchema>

export function WelcomeRoute() {
  const currentUser = useCurrentUser()

  const needsName =
    currentUser.name === null ||
    currentUser.name === undefined ||
    currentUser.name.trim().length === 0

  if (currentUser.tos_accepted) {
    return <Navigate to="/" replace />
  }

  return (
    <Splash position="relative">
      <AuthSplashSignOutButton />
      <Box marginX="auto">
        <div className="w-96">
          <Card>
            <Stack gap="md">
              <h1 className="text-tremor-content-emphasis font-serif text-2xl font-normal">
                {`Welcome to Joyful Health!`}
              </h1>

              {needsName ? <WelcomeWithNameForm /> : <WelcomeTosOnly />}
            </Stack>
          </Card>
        </div>
      </Box>
    </Splash>
  )
}

function useAcceptTosMutation() {
  const client = useApiQueryClient()
  const { invalidateApiQueries } = client
  return useApiMutation('post', '/users/accept_tos', {
    onError: (error) => {
      toast.error(extractErrorMessage(error))
    },
    onSuccess: async () => {
      await invalidateApiQueries('/users/me')
    },
  })
}

function WelcomeTosOnly() {
  const [checked, setChecked] = useState(false)
  const acceptTosMutation = useAcceptTosMutation()

  return (
    <Form
      className="flex flex-col gap-6"
      onSubmit={(event) => {
        event?.preventDefault()
        if (!checked) {
          return
        }

        acceptTosMutation.mutate({})
      }}
    >
      <div className="flex items-center gap-2">
        <LabeledCheckbox
          disabled={acceptTosMutation.isPending}
          id="tosChbx"
          labelProps={{ size: 'lg' }}
          checked={checked}
          onCheckedChange={() => {
            setChecked(!checked)
          }}
        >
          <span>
            {`Accept our `}
            <TextLink
              to="https://www.joyfulhealth.com/terms-conditions"
              endIcon={<ExternalLinkIcon />}
            >
              {`Terms of Service`}
            </TextLink>
          </span>
        </LabeledCheckbox>
      </div>

      <Button
        width="full"
        type="submit"
        variant="brand-subtle"
        disabled={!checked}
        isPending={acceptTosMutation.isPending || acceptTosMutation.isSuccess}
      >
        {`Continue`}
      </Button>
    </Form>
  )
}

function WelcomeWithNameForm() {
  const [tosChecked, setTosChecked] = useState(false)
  const acceptTosMutation = useAcceptTosMutation()

  const form = useForm<WelcomeNameValues>({
    defaultValues: {
      firstName: '',
      lastName: '',
    },
    resolver: zodResolver(WelcomeNameSchema),
  })

  const isPending = acceptTosMutation.isPending || acceptTosMutation.isSuccess

  return (
    <FormProvider {...form}>
      <Form
        className="flex flex-col gap-6"
        onSubmit={form.handleSubmit((values) => {
          const firstName = values.firstName.trim() || null
          const lastName = values.lastName.trim() || null
          acceptTosMutation.mutate({
            body:
              firstName === null && lastName === null
                ? null
                : {
                    first_name: firstName,
                    last_name: lastName,
                  },
          })
        })}
      >
        <Fieldset disabled={isPending} className="flex flex-col gap-y-4">
          <FormInputField
            control={form.control}
            name="firstName"
            label="First name"
            inputProps={{
              autoComplete: 'given-name',
              autoFocus: true,
            }}
          />
          <FormInputField
            control={form.control}
            name="lastName"
            label="Last name"
            inputProps={{
              autoComplete: 'family-name',
            }}
          />
        </Fieldset>

        <div className="flex items-center gap-2">
          <LabeledCheckbox
            disabled={isPending}
            id="tosChbx"
            labelProps={{ size: 'lg' }}
            checked={tosChecked}
            onCheckedChange={() => {
              setTosChecked(!tosChecked)
            }}
          >
            <span>
              {`Accept our `}
              <TextLink
                to="https://www.joyfulhealth.com/terms-conditions"
                endIcon={<ExternalLinkIcon />}
              >
                {`Terms of Service`}
              </TextLink>
            </span>
          </LabeledCheckbox>
        </div>

        <Button
          width="full"
          type="submit"
          variant="brand-subtle"
          disabled={!tosChecked}
          isPending={isPending}
        >
          Continue
        </Button>
      </Form>
    </FormProvider>
  )
}
